import { useContext } from 'react'
import { useIntl } from 'react-intl'

import type { ItemCardVariant } from '../types'

import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import ViewMoreOffersCta from 'tripPlanner/components/Buttons/ViewMoreOffersCta'
import type { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'
import LEOfferCTAs from 'tripPlanner/components/ItemCard/Common/LEOfferCTAs'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import TripContext from 'tripPlanner/contexts/TripContext'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item:
    | Utils.RequiredProp<AccommodationItem, 'savedItemData'>
  isOfferAvailable: boolean
  variant: ItemCardVariant | DetailItemCardVariant
  offerLink: string
}

export default function PlannedHotelCtaContent(props: Props) {
  const intl = useIntl()
  const { isOfferAvailable, item, offerLink } = props
  const { startDate: checkIn, endDate: checkOut, code: offerId, tripId } = item
  const canEdit = useCanEditTrip()
  const { viewOfferCopy } = useTripPlannerCTALabels()
  const tripContext = useContext(TripContext)
  const isPublicTrip = tripContext?.isPublicTrip
  const buttonSize = props.variant == 'mapSummary' ? 'small' : 'medium'

  if (isPublicTrip) {
    return (
      <Group
        direction={props.variant === 'mapSummary' ? 'vertical' : 'horizontal'}
        gap={12}
      >
        <SaveTripItemButton item={item} />
        <TextButton kind="primary" to={offerLink} target="_blank">
          {viewOfferCopy}
        </TextButton>
      </Group>
    )
  } else if (!isOfferAvailable) {
    const hasPlannedInfo = !!checkIn || !!checkOut

    return (
      <Group
        direction="horizontal"
        gap={8}
        tabletHorizontalAlign="space-between"
        horizontalAlign="stretch"
      >
        {hasPlannedInfo && (
          <TextButton kind="tertiary" to={addTripIdParam(offerLink, tripId, canEdit)} target="_blank" size={buttonSize}>
            {intl.formatMessage({
              id: 'O5BbVkpWFCx_NunZ5nMc',
              defaultMessage: 'Change details',
              description: 'Button to change booking details for a planned hotel trip item',
            })}
          </TextButton>
        )}
        <ViewMoreOffersCta
          offerId={offerId}
          checkIn={checkIn}
          checkOut={checkOut}
          sectionId={tripItemSelectionId(item.id)}
          tripId={item.tripId}
          showSearch={props.variant === 'tripSummary'}
          size={buttonSize}
        />
      </Group>
    )
  } else {
    return <LEOfferCTAs item={item} variant={props.variant} />
  }
}
